import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Badge,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@asm/ui";
import { scansApi, domainsApi, type Domain } from "@/lib/api";
import { formatModuleName } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import {
  Globe,
  Shield,
  Mail,
  Lock,
  FileCode,
  Cloud,
  Network,
  Code,
  GitBranch,
  FileSearch,
  Fingerprint,
  ChevronRight,
  Zap,
  CheckCircle2,
  AlertTriangle,
  ArrowLeft,
  Radar,
  AlertOctagon,
  MapPin,
  Radio,
  Bug,
} from "lucide-react";

// Module type definition
type ScanModuleConfig = {
  id: string;
  name: string;
  description: string;
  icon: React.ElementType;
  category: "passive" | "active";
  color: string;
  requiresVerification?: boolean;
};

// Module definitions with icons and categories
const SCAN_MODULES: ScanModuleConfig[] = [
  {
    id: "dns",
    name: "DNS",
    description: "DNS records, subdomain enumeration & CT logs",
    icon: Globe,
    category: "passive",
    color: "cyan",
  },
  {
    id: "email",
    name: "Email Security",
    description: "SPF, DKIM, DMARC analysis",
    icon: Mail,
    category: "passive",
    color: "cyan",
  },
  {
    id: "tls",
    name: "TLS/SSL",
    description: "Certificate analysis",
    icon: Lock,
    category: "passive",
    color: "cyan",
  },
  {
    id: "headers",
    name: "HTTP Headers",
    description: "Security headers analysis",
    icon: FileCode,
    category: "passive",
    color: "cyan",
  },
  {
    id: "whois",
    name: "WHOIS",
    description: "Domain registration info",
    icon: Shield,
    category: "passive",
    color: "cyan",
  },
  {
    id: "cloud",
    name: "Cloud Assets",
    description: "S3, Azure, GCS bucket discovery",
    icon: Cloud,
    category: "passive",
    color: "cyan",
  },
  {
    id: "asn",
    name: "ASN/BGP",
    description: "IP ranges and network intelligence",
    icon: Network,
    category: "passive",
    color: "cyan",
  },
  {
    id: "tech",
    name: "Technology",
    description: "Web technology fingerprinting",
    icon: Code,
    category: "passive",
    color: "cyan",
  },
  {
    id: "repos",
    name: "Repository Exposure",
    description: "GitHub/GitLab secret scanning",
    icon: GitBranch,
    category: "passive",
    color: "cyan",
  },
  {
    id: "crawl_hints",
    name: "Crawl Hints",
    description: "robots.txt, sitemap, security.txt",
    icon: FileSearch,
    category: "passive",
    color: "cyan",
  },
  {
    id: "js_analysis",
    name: "JS Analysis",
    description: "API endpoints, secrets, third-party",
    icon: Fingerprint,
    category: "passive",
    color: "cyan",
  },
  {
    id: "favicon",
    name: "Favicon Hash",
    description: "Application fingerprinting",
    icon: Fingerprint,
    category: "passive",
    color: "cyan",
  },
  {
    id: "typosquat",
    name: "Typosquatting",
    description: "Lookalike domain detection",
    icon: AlertOctagon,
    category: "passive",
    color: "cyan",
  },
  {
    id: "ipintel",
    name: "IP Intelligence",
    description: "Geolocation, security & threat data",
    icon: MapPin,
    category: "passive",
    color: "cyan",
  },
  {
    id: "vuln",
    name: "Vulnerability Scan",
    description: "CVE correlation for detected services",
    icon: Bug,
    category: "passive",
    color: "cyan",
  },
  {
    id: "portscan",
    name: "Port Scan",
    description: "TCP/UDP port scanning and service detection",
    icon: Radio,
    category: "active",
    color: "amber",
    requiresVerification: true,
  },
  {
    id: "service_probe",
    name: "Service Probe",
    description: "Deep service fingerprinting and version detection",
    icon: Fingerprint,
    category: "active",
    color: "amber",
    requiresVerification: true,
  },
  {
    id: "subdomains_bruteforce",
    name: "Subdomain Bruteforce",
    description: "Active subdomain discovery with adaptive learning",
    icon: Radar,
    category: "active",
    color: "amber",
    requiresVerification: true,
  },
  {
    id: "cloud_misconfig",
    name: "Cloud Security",
    description: "Deep security analysis of cloud storage buckets",
    icon: Cloud,
    category: "active",
    color: "amber",
    requiresVerification: true,
  },
];

// Module card component
function ModuleCard({
  module,
  isSelected,
  isDisabled,
  onToggle,
  index,
}: {
  module: (typeof SCAN_MODULES)[0];
  isSelected: boolean;
  isDisabled: boolean;
  onToggle: () => void;
  index: number;
}) {
  const Icon = module.icon;
  const isActive = module.category === "active";

  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={isDisabled}
      className={`
        module-card group relative p-4 rounded-lg text-left
        animate-fade-in-up stagger-${(index % 8) + 1}
        ${isSelected ? "selected glow-cyan" : ""}
        ${isDisabled ? "cursor-not-allowed" : "cursor-pointer"}
        ${isActive ? "border-l-2 border-l-amber-500/50" : ""}
      `}
    >
      {/* Corner accents for selected state */}
      {isSelected && <div className="corner-accent absolute inset-0 pointer-events-none" />}

      {/* Module icon and status */}
      <div className="flex items-start justify-between mb-3">
        <div
          className={`
            p-2 rounded-md transition-colors
            ${isSelected
              ? "bg-cyan-500/20 text-cyan-400"
              : isDisabled
                ? "bg-muted/50 text-muted-foreground/60"
                : "bg-muted text-muted-foreground group-hover:text-cyan-400"
            }
          `}
        >
          <Icon className="h-5 w-5" />
        </div>

        {/* Status indicators */}
        <div className="flex items-center gap-2">
          {isActive && (
            <Badge
              variant="outline"
              className={`text-[10px] px-1.5 ${
                isDisabled
                  ? "border-amber-500/30 text-amber-400/60"
                  : "border-amber-500/50 text-amber-400"
              }`}
            >
              ACTIVE
            </Badge>
          )}
          {isDisabled && (
            <Lock className="h-4 w-4 text-amber-400/70" />
          )}
          {isSelected && !isDisabled && (
            <CheckCircle2 className="h-4 w-4 text-cyan-400" />
          )}
        </div>
      </div>

      {/* Module name */}
      <h3
        className={`
          font-semibold text-sm mb-1 transition-colors
          ${isSelected ? "text-cyan-50" : isDisabled ? "text-foreground/60" : "text-foreground"}
        `}
      >
        {formatModuleName(module.id)}
      </h3>

      {/* Description */}
      <p className={`text-xs leading-relaxed ${isDisabled ? "text-muted-foreground/60" : "text-muted-foreground"}`}>
        {module.description}
      </p>

      {/* Locked indicator for disabled active modules */}
      {isDisabled && (
        <div className="mt-3 pt-2 border-t border-amber-500/20 flex items-center gap-1.5 text-[10px] text-amber-400/70">
          <Lock className="h-3 w-3" />
          <span>Verify domain to unlock</span>
        </div>
      )}
    </button>
  );
}

export default function NewScan() {
  const { t } = useTranslation("scans");
  const [selectedDomain, setSelectedDomain] = useState("");
  const [selectedModules, setSelectedModules] = useState<string[]>([
    "dns",
    "email",
    "tls",
    "headers",
  ]);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Fetch domains
  const { data: domains, isLoading: domainsLoading } = useQuery({
    queryKey: ["domains"],
    queryFn: domainsApi.list,
  });

  // Get selected domain details
  const selectedDomainData = useMemo(() => {
    return domains?.find((d: Domain) => d.id === selectedDomain);
  }, [domains, selectedDomain]);

  // Active modules are allowed based on backend calculation (verification or org authorization)
  const isVerified = selectedDomainData?.active_modules_allowed === true;

  const createScan = useMutation({
    mutationFn: scansApi.create,
    onSuccess: (scan) => {
      queryClient.invalidateQueries({ queryKey: ["scans"] });
      navigate(`/scans/${scan.id}`);
    },
  });

  const toggleModule = (moduleId: string) => {
    const module = SCAN_MODULES.find((m) => m.id === moduleId);
    if (module?.requiresVerification && !isVerified) return;

    setSelectedModules((prev) =>
      prev.includes(moduleId)
        ? prev.filter((m) => m !== moduleId)
        : [...prev, moduleId]
    );
  };

  const selectAllPassive = () => {
    const passiveModules = SCAN_MODULES.filter((m) => m.category === "passive").map((m) => m.id);
    setSelectedModules(passiveModules);
  };

  const selectAll = () => {
    if (!isVerified) {
      selectAllPassive();
      return;
    }
    setSelectedModules(SCAN_MODULES.map((m) => m.id));
  };

  const clearSelection = () => {
    setSelectedModules([]);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedDomain || selectedModules.length === 0) return;

    const domain = domains?.find((d: Domain) => d.id === selectedDomain);
    if (!domain) return;

    // Determine mode based on selected modules
    // Active modules (portscan, subdomains) require self_assessment mode
    const hasActiveModules = selectedModules.some((id) =>
      SCAN_MODULES.find((m) => m.id === id)?.category === "active"
    );
    const mode = hasActiveModules ? "self_assessment" : "passive";

    createScan.mutate({ target: domain.domain, modules: selectedModules, mode });
  };

  // Sort domains: verified first, then unverified
  const sortedDomains = useMemo(() => {
    const all = domains || [];
    const verified = all.filter((d: Domain) => d.verified_at != null);
    const unverified = all.filter((d: Domain) => d.verified_at == null);
    return { verified, unverified, all };
  }, [domains]);

  const availableDomains = sortedDomains.all;
  const passiveCount = selectedModules.filter((id) =>
    SCAN_MODULES.find((m) => m.id === id)?.category === "passive"
  ).length;
  const activeCount = selectedModules.length - passiveCount;

  // Determine display mode based on module mix
  const scanModeDisplay = useMemo(() => {
    if (selectedModules.length === 0) return { label: "—", color: "text-muted-foreground" };
    if (activeCount === 0) return { label: "Passive", color: "text-cyan-400" };
    if (passiveCount === 0) return { label: "Active", color: "text-amber-400" };
    return { label: "Mixed", color: "text-purple-400" };
  }, [selectedModules.length, passiveCount, activeCount]);

  return (
    <div className="tactical-grid">
      {/* Header section */}
      <div className="animate-fade-in-up mb-8">
        <button
          onClick={() => navigate("/scans")}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4 transition-colors text-sm"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Scans
        </button>

        <div className="flex items-center gap-4">
          <div className="p-3 rounded-lg bg-cyan-500/10 border border-cyan-500/20">
            <Radar className="h-8 w-8 text-cyan-400" />
          </div>
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-foreground">
              {t("new.title", "New Scan")}
            </h1>
            <p className="text-muted-foreground">
              {t("new.description", "Configure and launch a security reconnaissance scan")}
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Error display */}
        {createScan.error && (
          <div className="animate-fade-in-up p-4 rounded-lg border border-destructive/50 bg-destructive/10 flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            <p className="text-sm text-destructive">
              {createScan.error instanceof Error
                ? createScan.error.message
                : t("new.failed", "Failed to create scan")}
            </p>
          </div>
        )}

        {/* Target selection card */}
        <div className="animate-fade-in-up stagger-1 rounded-lg border border-border bg-card/50 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-8 w-1 rounded-full bg-cyan-500" />
            <h2 className="text-lg font-semibold">Target Selection</h2>
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground">
                {t("new.domain", "Target Domain")}
              </label>
              <Select
                value={selectedDomain}
                onValueChange={setSelectedDomain}
                disabled={domainsLoading}
              >
                <SelectTrigger className="h-12 bg-muted/50 border-border hover:border-cyan-500/50 transition-colors">
                  <SelectValue placeholder={t("new.selectDomain", "Select a domain")} />
                </SelectTrigger>
                <SelectContent>
                  {/* Verified domains first */}
                  {sortedDomains.verified.length > 0 && (
                    <>
                      <div className="px-2 py-1.5 text-xs font-medium text-emerald-400/80 flex items-center gap-1.5">
                        <CheckCircle2 className="h-3 w-3" />
                        Verified
                      </div>
                      {sortedDomains.verified.map((domain: Domain) => (
                        <SelectItem key={domain.id} value={domain.id}>
                          <div className="flex items-center gap-2">
                            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400 flex-shrink-0" />
                            <span className="font-mono">{domain.domain}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </>
                  )}
                  {/* Unverified domains */}
                  {sortedDomains.unverified.length > 0 && (
                    <>
                      {sortedDomains.verified.length > 0 && (
                        <div className="my-1 border-t border-border" />
                      )}
                      <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                        <AlertTriangle className="h-3 w-3" />
                        Unverified
                      </div>
                      {sortedDomains.unverified.map((domain: Domain) => (
                        <SelectItem key={domain.id} value={domain.id}>
                          <div className="flex items-center gap-2">
                            <Globe className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                            <span className="font-mono">{domain.domain}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </>
                  )}
                </SelectContent>
              </Select>

              {availableDomains.length === 0 && !domainsLoading && (
                <p className="text-xs text-amber-400 flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" />
                  {t("new.noDomains", "No domains available. Please add a domain first.")}
                </p>
              )}
            </div>

            {/* Domain status card */}
            {selectedDomainData && (
              <div className="rounded-lg border border-border bg-muted/30 p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm text-muted-foreground">Domain Status</span>
                  {isVerified ? (
                    <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
                      <CheckCircle2 className="h-3 w-3 mr-1" />
                      Active Enabled
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-amber-400 border-amber-500/30">
                      <AlertTriangle className="h-3 w-3 mr-1" />
                      Passive Only
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  {isVerified
                    ? "Full access to all scan modules including active reconnaissance."
                    : "Limited to passive modules. Verify domain for active scanning."}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Modules section */}
        <div className="animate-fade-in-up stagger-2 rounded-lg border border-border bg-card/50 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="h-8 w-1 rounded-full bg-cyan-500" />
              <div>
                <h2 className="text-lg font-semibold">Scan Modules</h2>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {selectedModules.length} selected • {passiveCount} passive
                  {activeCount > 0 && ` • ${activeCount} active`}
                </p>
              </div>
            </div>

            {/* Quick actions */}
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={clearSelection}
                className="text-xs h-8"
              >
                Clear
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={selectAllPassive}
                className="text-xs h-8"
              >
                All Passive
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={selectAll}
                className="text-xs h-8"
                disabled={!isVerified}
              >
                Select All
              </Button>
            </div>
          </div>

          {/* Module grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
            {SCAN_MODULES.map((module, index) => (
              <ModuleCard
                key={module.id}
                module={module}
                isSelected={selectedModules.includes(module.id)}
                isDisabled={Boolean(module.requiresVerification) && !isVerified}
                onToggle={() => toggleModule(module.id)}
                index={index}
              />
            ))}
          </div>

          {/* Legend */}
          <div className="flex items-center gap-6 mt-6 pt-4 border-t border-border">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <div className="w-3 h-3 rounded-sm bg-cyan-500/20 border border-cyan-500/40" />
              <span>Passive modules</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <div className="w-3 h-3 rounded-sm bg-amber-500/20 border border-amber-500/40" />
              <span>Active modules</span>
            </div>
          </div>
        </div>

        {/* Action bar */}
        <div className="animate-fade-in-up stagger-3 flex items-center justify-between rounded-lg border border-border bg-card/50 p-4">
          <div className="flex items-center gap-4">
            <div className="text-sm">
              <span className="text-muted-foreground">Target: </span>
              <span className="font-mono text-foreground">
                {selectedDomainData?.domain || "—"}
              </span>
            </div>
            <div className="h-4 w-px bg-border" />
            <div className="text-sm">
              <span className="text-muted-foreground">Modules: </span>
              <span className="text-foreground font-medium">
                {selectedModules.length}
              </span>
            </div>
            <div className="h-4 w-px bg-border" />
            <div className="text-sm">
              <span className="text-muted-foreground">Mode: </span>
              <span className={`font-medium ${scanModeDisplay.color}`}>
                {scanModeDisplay.label}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Button
              type="button"
              variant="ghost"
              onClick={() => navigate("/scans")}
              className="h-10"
            >
              {t("new.cancel", "Cancel")}
            </Button>
            <Button
              type="submit"
              disabled={!selectedDomain || selectedModules.length === 0 || createScan.isPending}
              className="h-10 px-6 bg-cyan-500 hover:bg-cyan-400 text-black font-semibold glow-cyan"
            >
              {createScan.isPending ? (
                <>
                  <Zap className="h-4 w-4 mr-2 animate-pulse" />
                  {t("new.starting", "Initializing...")}
                </>
              ) : (
                <>
                  <ChevronRight className="h-4 w-4 mr-1" />
                  {t("new.start", "Launch Scan")}
                </>
              )}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
